import { useEffect, useState, type FormEvent } from 'react'
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconHistory, IconMessageCircle2, IconSend, IconUserCircle, IconCalendarEvent, IconPencil } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import DatePicker, { registerLocale } from 'react-datepicker'
import { ru } from 'date-fns/locale'
import 'react-datepicker/dist/react-datepicker.css'
import './task-modal-datepicker.css'
import { fetchTask, setTaskDue, updateTask } from '../../shared/api/tasks'
import { createComment, fetchComments } from '../../shared/api/comments'
import { TaskHistory } from './task-history'
import { TaskAttachments } from './task-attachments'

registerLocale('ru', ru)

interface Props {
  taskId: string | null
  onClose: () => void
  /** #8: открыть другую задачу (подзадачу) в той же модалке — закрытие/перемещение в её карточке. */
  onOpenTask?: (id: string) => void
}


export function TaskModal({ taskId, onClose, onOpenTask }: Props) {
  const qc = useQueryClient()

  const taskQ = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => fetchTask(taskId!),
    enabled: !!taskId,
  })
  const commentsQ = useQuery({
    queryKey: ['task', taskId, 'comments'],
    queryFn: () => fetchComments(taskId!),
    enabled: !!taskId,
  })

  const [draft, setDraft] = useState('')
  const sendComment = useMutation({
    mutationFn: (body: string) => createComment(taskId!, { body }),
    onSuccess: () => {
      setDraft('')
      qc.invalidateQueries({ queryKey: ['task', taskId, 'comments'] })
      qc.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })

  function submitComment(e: FormEvent) {
    e.preventDefault()
    if (!draft.trim()) return
    sendComment.mutate(draft.trim())
  }

  // ── Edit-режим: title/description/priority/due_at ──
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editPriority, setEditPriority] = useState<'low' | 'med' | 'high' | 'urgent'>('med')
  const [editDueAt, setEditDueAt] = useState<Date | null>(null)

  // Подгружаем текущие значения при открытии edit-режима
  useEffect(() => {
    if (editing && taskQ.data) {
      setEditTitle(taskQ.data.title)
      setEditDescription(taskQ.data.description ?? '')
      setEditPriority(taskQ.data.priority as 'low' | 'med' | 'high' | 'urgent')
      setEditDueAt(taskQ.data.due_at ? new Date(taskQ.data.due_at) : null)
    }
  }, [editing, taskQ.data])

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!taskId || !taskQ.data) return
      // 1. POST /due ПЕРВЫМ: смена срока автопересчитывает приоритет на сервере;
      //    явный выбор приоритета (шаг 2) должен его перекрыть, а не наоборот.
      const oldDue = taskQ.data.due_at ?? null
      const newDue = editDueAt ? editDueAt.toISOString() : null
      if (oldDue !== newDue) {
        await setTaskDue(taskId, newDue)
      }
      // 2. PATCH title/desc; priority — только если пользователь его менял,
      //    иначе нетронутый select затирал бы автоприоритет ручным флагом.
      await updateTask(taskId, {
        title: editTitle.trim() || taskQ.data.title,
        description: editDescription.trim() ? editDescription.trim() : null,
        ...(editPriority !== taskQ.data.priority ? { priority: editPriority } : {}),
      })
    },
    onSuccess: () => {
      notifications.show({ message: 'Изменения сохранены', color: 'green' })
      setEditing(false)
      qc.invalidateQueries({ queryKey: ['task', taskId] })
      qc.invalidateQueries({ queryKey: ['board'] }) // на канбане title мог измениться
    },
    onError: (e: unknown) => {
      const msg = (e instanceof Error && e.message) || 'Не удалось сохранить'
      notifications.show({ message: msg, color: 'red' })
    },
  })

  return (
    <Modal
      opened={!!taskId}
      onClose={onClose}
      size="xl"
      title={<Text fw={700}>Задача</Text>}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {taskQ.isLoading ? (
        <Loader />
      ) : !taskQ.data ? (
        <Text c="red">Не удалось загрузить задачу</Text>
      ) : (
        <Stack gap="md">
          <Group gap="xs" justify="space-between">
            <Group gap="xs">
              <Badge color={taskQ.data.column.is_done_column ? 'green' : taskQ.data.column.is_inbox_column ? 'gray' : 'blue'} variant="light">
                {taskQ.data.column.name}
              </Badge>
              <Badge color={priorityColor(taskQ.data.priority)} variant="dot">
                {priorityLabel(taskQ.data.priority)}
              </Badge>
              {taskQ.data.due_at && (
                <Badge
                  color={dueColor(taskQ.data.due_state)}
                  variant="light"
                  leftSection={<IconCalendarEvent size={11} />}
                >
                  {dayjs(taskQ.data.due_at).format('D MMM YYYY HH:mm')}
                </Badge>
              )}
            </Group>
            {!editing && (
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => setEditing(true)}
                title="Редактировать задачу"
              >
                <IconPencil size={16} />
              </ActionIcon>
            )}
          </Group>

          {editing ? (
            <Stack gap="sm">
              <TextInput
                label="Название"
                value={editTitle}
                onChange={(e) => setEditTitle(e.currentTarget.value)}
                required
              />
              <Textarea
                label="Описание"
                value={editDescription}
                onChange={(e) => setEditDescription(e.currentTarget.value)}
                autosize
                minRows={3}
                maxRows={10}
                placeholder="— нет —"
              />
              <Group grow>
                <Select
                  label="Приоритет"
                  data={[
                    { value: 'low', label: 'Низкий' },
                    { value: 'med', label: 'Средний' },
                    { value: 'high', label: 'Высокий' },
                    { value: 'urgent', label: 'Срочный' },
                  ]}
                  value={editPriority}
                  onChange={(v) => v && setEditPriority(v as 'low' | 'med' | 'high' | 'urgent')}
                  allowDeselect={false}
                />
                {/* react-datepicker — чистый календарь macOS-style. Кастомизирован под Mantine
                    через task-modal.module.css (см. globals override ниже). Русская локаль,
                    24-часовой формат, выбор времени интервалом 15 мин. */}
                <Stack gap={4}>
                  <Text size="sm" fw={500}>Дедлайн</Text>
                  <Group gap="xs" wrap="nowrap" align="center">
                    <DatePicker
                      selected={editDueAt}
                      onChange={setEditDueAt}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="d MMMM yyyy, HH:mm"
                      locale="ru"
                      timeCaption="Время"
                      placeholderText="не задан"
                      isClearable
                      className="rdp-input"
                      popperPlacement="bottom-start"
                    />
                  </Group>
                </Stack>
              </Group>
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setEditing(false)} disabled={saveMut.isPending}>
                  Отмена
                </Button>
                <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!editTitle.trim()}>
                  Сохранить
                </Button>
              </Group>
            </Stack>
          ) : (
            <>
              <Title order={3}>
                <Text span c="dimmed" inherit mr={6}>#{taskQ.data.human_id}</Text>
                {taskQ.data.title}
              </Title>
              {taskQ.data.description && (
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {taskQ.data.description}
                </Text>
              )}
            </>
          )}

          <Group gap="lg">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Исполнитель
              </Text>
              <Group gap={6}>
                {taskQ.data.assignee ? (
                  <>
                    <Avatar size={22} radius="xl" color="blue">
                      {taskQ.data.assignee.full_name[0]}
                    </Avatar>
                    <Text size="sm">{taskQ.data.assignee.full_name}</Text>
                  </>
                ) : (
                  <Text size="sm" c="dimmed">
                    <IconUserCircle size={16} /> не назначен
                  </Text>
                )}
              </Group>
            </Stack>

            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Постановщик
              </Text>
              <Group gap={6}>
                <Avatar size={22} radius="xl" color="grape">
                  {taskQ.data.reporter.full_name[0]}
                </Avatar>
                <Text size="sm">{taskQ.data.reporter.full_name}</Text>
              </Group>
            </Stack>

            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Проект
              </Text>
              <Text size="sm" fw={500}>
                {taskQ.data.project.name}
              </Text>
            </Stack>
          </Group>

          {taskId && <TaskAttachments taskId={taskId} attachments={taskQ.data.attachments} />}

          {/* #8: подзадачи родителя — на доске их нет, видны только здесь. Клик открывает
              подзадачу (закрытие/перемещение — в её карточке; веб-создание вне v1). */}
          {taskQ.data.subtasks.length > 0 && (
            <>
              <Divider />
              <Stack gap={6}>
                <Text fw={600} size="sm">
                  Подзадачи · {taskQ.data.subtasks.filter((s) => s.is_done).length}/{taskQ.data.subtasks.length}
                </Text>
                {taskQ.data.subtasks.map((s) => (
                  <Group
                    key={s.id}
                    gap="xs"
                    wrap="nowrap"
                    style={{ cursor: onOpenTask ? 'pointer' : 'default', opacity: s.is_done ? 0.6 : 1 }}
                    onClick={() => onOpenTask?.(s.id)}
                  >
                    <Text size="sm">{s.is_done ? '✅' : '⬜️'}</Text>
                    <Text size="sm" td={s.is_done ? 'line-through' : undefined} style={{ flex: 1, minWidth: 0 }} truncate>
                      #{s.human_id} {s.title}
                    </Text>
                    <Text size="xs" c="dimmed">{s.assignee?.full_name ?? '—'}</Text>
                    {s.due_at && <Text size="xs" c="dimmed">{dayjs(s.due_at).format('D MMM')}</Text>}
                  </Group>
                ))}
              </Stack>
            </>
          )}

          <Divider />

          <Tabs defaultValue="comments" keepMounted={false}>
            <Tabs.List mb="sm">
              <Tabs.Tab value="comments" leftSection={<IconMessageCircle2 size={14} />}>
                Комментарии · {commentsQ.data?.length ?? 0}
              </Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
                История
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="comments">
              <Stack gap="md">
          {commentsQ.data?.map((c) => (
            <Stack key={c.id} gap={4}>
              <Group gap={6}>
                <Avatar size={22} radius="xl" color="blue">
                  {c.author.full_name[0]}
                </Avatar>
                <Text size="sm" fw={600}>
                  {c.author.full_name}
                </Text>
                <Text size="xs" c="dimmed">
                  {dayjs(c.created_at).format('D MMM HH:mm')}
                  {c.edited_at && ' · отредактирован'}
                </Text>
              </Group>
              <Text size="sm" pl={28} style={{ whiteSpace: 'pre-wrap' }}>
                {c.body}
              </Text>
            </Stack>
          ))}

          {commentsQ.data && commentsQ.data.length === 0 && (
            <Text size="xs" c="dimmed" pl={28}>
              Комментариев пока нет
            </Text>
          )}

          <form onSubmit={submitComment}>
            <Group align="flex-end" gap="xs">
              <Textarea
                placeholder="Написать комментарий…"
                autosize
                minRows={2}
                style={{ flex: 1 }}
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
              />
              <Button
                type="submit"
                disabled={!draft.trim()}
                loading={sendComment.isPending}
                leftSection={<IconSend size={16} />}
              >
                Отправить
              </Button>
            </Group>
          </form>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="history">
              {taskId && <TaskHistory taskId={taskId} />}
            </Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Modal>
  )
}

function priorityColor(p: string): string {
  return { low: 'gray', med: 'blue', high: 'orange', urgent: 'red' }[p] ?? 'gray'
}
function priorityLabel(p: string): string {
  return { low: 'Низкий', med: 'Средний', high: 'Высокий', urgent: 'Срочный' }[p] ?? p
}
function dueColor(s: string): string {
  return { none: 'gray', ok: 'green', soon: 'yellow', overdue: 'red' }[s] ?? 'gray'
}

// Чтобы TextInput не был unused-warning (не используется в этом MVP-модале)
void TextInput
